import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../Components/Navbar/Navbar';
import Footer from '../Components/Footer/Footer';
import Background from '../Components/Background/Background';
import './ProjectResults.css';
import projectResultsBg from '../Assets/1043950.jpg';
import ModulePDF from '../Components/Section/ModulePDF';

// Dutch PDFs
const nlModule1 = '/Assets/Dutch/NL%20MODULE%201.pdf';
const nlModule2 = '/Assets/Dutch/NL%20MODULE%202.pdf';
const nlModule3 = '/Assets/Dutch/NL%20MODULE%203.pdf';
const nlModule4 = '/Assets/Dutch/NL%20MODULE%204.pdf';
const nlModule5 = '/Assets/Dutch/NL%20MODULE%205.pdf';

// German PDFs
const deModule1 = '/Assets/German/Green%20Transitions%20DEU%20Modul%201.pdf';
const deModule2 = '/Assets/German/Green%20Transitions%20DEU%20Modul%202.pdf';
const deModule3 = '/Assets/German/Green%20Transitions%20DEU%20Modul%203.pdf';
const deModule4 = '/Assets/German/Green%20Transitions%20DEU%20Modul%204.pdf';
const deModule5 = '/Assets/German/Green%20Transitions%20DEU%20Modul%205.pdf';

// Greek PDFs
const elModule1 = '/Assets/Greek/Module%201%20GR.pdf';
const elModule2 = '/Assets/Greek/Module%202%20GR.pdf';
const elModule3 = '/Assets/Greek/Module%203%20GR.pdf';
const elModule4 = '/Assets/Greek/Module%204%20GR.pdf';
const elModule5 = '/Assets/Greek/Module%205%20GR.pdf';

// Portuguese PDFs
const ptModule1 = '/Assets/Portugal/PT%20MODULE%201.pdf';
const ptModule2 = '/Assets/Portugal/PT%20MODULE%202.pdf';
const ptModule3 = '/Assets/Portugal/PT%20MODULE%203.pdf';
const ptModule4 = '/Assets/Portugal/PT%20MODULE%204.pdf';
const ptModule5 = '/Assets/Portugal/PT%20MODULE%205.pdf';

// Slovak PDFs
const skModule1 = '/Assets/Slovak/SLOVAK%20MODULE%201.pdf';
const skModule2 = '/Assets/Slovak/SLOVAK%20MODULE%202.pdf';
const skModule3 = '/Assets/Slovak/SLOVAK%20MODULE%203.pdf';
const skModule4 = '/Assets/Slovak/SLOVAK%20MODULE%204.pdf';
const skModule5 = '/Assets/Slovak/SLOVAK%20MODULE%205.pdf';

// Handbook
const handbookPdf = '/Assets/WP2/GreenTransitions-Handbook-FINAL.pdf';


const ModuleCard = ({ module, isExpanded, toggleExpand }) => {
  const { i18n } = useTranslation();
  const language = i18n.language ? i18n.language.split('-')[0] : 'en';
  const currentPdf = module.pdfs[language];
  const displayUrl = currentPdf || module.embedUrl;
  const isPdf = !!currentPdf;

  return (
    <div className={`module-card ${isExpanded ? 'expanded' : ''}`}>
      <div className="module-header" onClick={() => toggleExpand(module.id)}>
        <h3>{module.title}</h3>
        <p>{module.subtitle}</p>
        <span className="expand-icon">{isExpanded ? '−' : '+'}</span>
      </div>
      
      {isExpanded && (
        <div className="module-content">
          <div style={{ width: '100%' }}>
            <div style={{ position: 'relative', paddingBottom: isPdf ? '141.4%' : '56.25%', paddingTop: 0, height: 0 }}>
              <iframe 
                title={`${module.title} - ${module.subtitle}`}
                frameBorder="0" 
                width="100%" 
                height="100%" 
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} 
                src={displayUrl} 
                type={isPdf ? "application/pdf" : "text/html"} 
                allowScriptAccess="always" 
                allowFullScreen={true} 
                scrolling="yes" 
                allowNetworking="all"
              ></iframe> 
            </div> 
            {module.videoUrl && (
              <div style={{ marginTop: '1.5rem', position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                <iframe 
                  src={module.videoUrl} 
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                  allow="autoplay"
                  title={`${module.title} - Video`}
                ></iframe>
              </div>
            )}
            <ModulePDF module={module} pdfs={module.pdfs} />
          </div>
        </div>
      )}
    </div>
  );
};


const ProjectResults = () => {
  const { t, i18n } = useTranslation('projectResults');
  const [expandedModuleId, setExpandedModuleId] = useState(null);
  const [activeSection, setActiveSection] = useState('wp2');

  // Get current language
  const language = i18n.language ? i18n.language.split('-')[0] : 'en';

  // Define WP3 Guide PDFs by language
  const guideFiles = {
    en: '/Assets/WP3/Guide/GreenTransitions WP3 Guide on IL.docx.pdf',
    nl: '/Assets/WP3/Guide/DUTCH_GreenTransitions WP3 Guide.pdf',
    de: '/Assets/WP3/Guide/GreenTransitions WP3 Guide on IL DEU.pdf',
    el: '/Assets/WP3/Guide/GreenTransitions WP3 Guide on IL - GR.pdf',
    pt: '/Assets/WP3/Guide/GreenTransitions WP3 Guide on IL pt-PT.pdf',
    sk: '/Assets/WP3/Guide/GreenTransitions WP3 Guide on IL_slovak.pdf',
  };

  // Define WP3 Tool PDFs by language
  const toolFiles = {
    en: '/Assets/WP3/Tool/Green Transitions - Tools.pdf',
    nl: '/Assets/WP3/Tool/Green Transitions - Tools final (Dutch).pdf',
    de: '/Assets/WP3/Tool/Green Transitions - Tools final DEU-1.pdf',
    el: '/Assets/WP3/Tool/Green Transitions - Tools final GR.pdf',
    pt: '/Assets/WP3/Tool/Green Transitions - Tools PT.pdf',
    sk: '/Assets/WP3/Tool/Green Transitions - Tools final_SK.pdf',
  };

  const currentGuide = guideFiles[language] || guideFiles.en;
  const currentTool = toolFiles[language] || toolFiles.en;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            entry.target.classList.add('visible');
          } else {
            entry.target.classList.remove('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '-20% 0px -20% 0px' }
    );

    const sections = document.querySelectorAll('.wp-section');
    sections.forEach((section) => observer.observe(section));

    return () => sections.forEach((section) => observer.unobserve(section));
  }, []);

  const MODULE_DATA = [
    {
      id: 1,
      title: "Module 1",
      subtitle: t('module1'),
      embedUrl: "https://view.genially.com/68aec6e457907729c9325a39",      videoUrl: "https://drive.google.com/file/d/1HjMNzoXxK6uVoswB9LmKh4p0C9lz40xK/preview",      pdfs: {
        nl: nlModule1,
        de: deModule1,
        el: elModule1,
        pt: ptModule1,
        sk: skModule1,
      }
    },
    {
      id: 2,
      title: "Module 2", 
      subtitle: t('module2'),
      embedUrl: "https://view.genially.com/68aeec7c68c440962fbfe2a9",
      videoUrl: "https://drive.google.com/file/d/1KDjb4nJEPEELNl0I4xGX9Dgzp9U56lhB/preview",
      pdfs: {
        nl: nlModule2,
        de: deModule2,
        el: elModule2,
        pt: ptModule2,
        sk: skModule2,
      }
    },
    {
      id: 3,
      title: "Module 3",
      subtitle: t('module3'),
      embedUrl: "https://view.genially.com/68aeef60b136aa1d3e772e81",
      videoUrl: "https://drive.google.com/file/d/1sjhT0PmsAysK60E_O0FAHTRrMVonFtOK/preview",
      pdfs: {
        nl: nlModule3,
        de: deModule3,
        el: elModule3,
        pt: ptModule3,
        sk: skModule3,
      }
    },
    {
      id: 4,
      title: "Module 4",
      subtitle: t('module4'),
      embedUrl: "https://view.genially.com/68aef09944422cf378c04502",
      videoUrl: "https://drive.google.com/file/d/1A5R4xK86_n6fZb87l6pekwnkQ-xtyFai/preview",
      pdfs: {
        nl: nlModule4,
        de: deModule4,
        el: elModule4,
        pt: ptModule4,
        sk: skModule4,
      }
    },
    {
      id: 5,
      title: "Module 5",
      subtitle: t('module5'),
      embedUrl: "https://view.genially.com/68aef4d568c440962fc6b3aa",
      videoUrl: "https://drive.google.com/file/d/15zpgEiwIhVY_rF2yJvWnPqapxY0_upUp/preview",
      pdfs: {
        nl: nlModule5,
        de: deModule5,
        el: elModule5,
        pt: ptModule5,
        sk: skModule5,
      }
    }
  ];

  const toggleExpand = (moduleId) => {
    setExpandedModuleId(expandedModuleId === moduleId ? null : moduleId);
  };

  return (
    <div className="project-results-container">
      <Background PlayState={true} customImage={projectResultsBg} />
      <Navbar />

      <div className="wp-sidebar">
        {['wp2', 'wp3', 'wp4'].map((section) => (
          <a
            key={section}
            href={`#${section}`}
            className={`wp-sidebar-icon ${activeSection === section ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              const element = document.getElementById(section);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            {section.toUpperCase()}
          </a>
        ))}
      </div>

      <div className="project-content">
        <h1 className="page-title">{t('title')}</h1>
        
        {/* WP2 Section */}
        <div id="wp2" className="wp-section">
          <div className="section-divider">
            <h2 className="section-title">{t('wp2Title')}</h2>
          </div>
          <div className="info-blocks-grid">
            <div className="info-block">
              <div className="info-block-header">
                <h2>{t('aboutCourseTitle')}</h2>
              </div>
              <div className="info-block-content">
                <p>
                  {t('aboutCourseText')}
                </p>
              </div>
            </div>

            <div className="info-block">
              <div className="info-block-header">
                <h2>{t('aboutHandbookTitle')}</h2>
              </div>
              <div className="info-block-content">
                <p>
                  {t('aboutHandbookText')}
                </p>
                <div style={{ marginTop: '1.5rem', position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                  <iframe 
                    src="https://drive.google.com/file/d/1sY34k-Lyhks3qn-pIAMkIfKTawAeZG2B/preview" 
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                    allow="autoplay"
                    title="Handbook Video"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>

          <div className="modules-grid">
            {MODULE_DATA.map((module) => (
              <ModuleCard
                key={module.id}
                module={module}
                isExpanded={expandedModuleId === module.id}
                toggleExpand={toggleExpand}
              />
            ))}
          </div>

          <div className="info-blocks-grid" style={{ marginTop: '3rem' }}>
            <div className="info-block">
              <div className="info-block-header">
                <h2>Green Transitions Handbook</h2>
              </div>
              <div className="info-block-content">
                <div style={{ marginTop: '1rem', height: '600px' }}>
                  <iframe 
                    src={handbookPdf}
                    width="100%" 
                    height="100%" 
                    style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                    title="Green Transitions Handbook"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* WP3 Section */}
        <div id="wp3" className="wp-section">
          <div className="section-divider">
            <h2 className="section-title">{t('wp3Title')}</h2>
          </div>
          <div className="info-blocks-grid">
            <div className="info-block">
              <div className="info-block-header">
                <h2>{t('aboutGuidelineTitle')}</h2>
              </div>
              <div className="info-block-content">
                <p>
                  {t('aboutGuidelineText')}
                </p>
                <div style={{ marginTop: '1.5rem', height: '600px' }}>
                  <iframe 
                    src={currentGuide}
                    width="100%" 
                    height="100%" 
                    style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                    title="Guideline PDF"
                  ></iframe>
                </div>
              </div>
            </div>

            <div className="info-block">
              <div className="info-block-header">
                <h2>{t('aboutToolTitle')}</h2>
              </div>
              <div className="info-block-content">
                <p>
                  {t('aboutToolText')}
                </p>
                <div style={{ marginTop: '1.5rem', height: '600px' }}>
                  <iframe 
                    src={currentTool}
                    width="100%" 
                    height="100%" 
                    style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                    title="Tool PDF"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* WP4 Section */}
        <div id="wp4" className="wp-section">
          <div className="section-divider">
            <h2 className="section-title">{t('wp4Title')}</h2>
          </div>
          <div className="info-blocks-grid">
            <div className="info-block">
              <div className="info-block-header">
                <h2>{t('aboutGameTitle')}</h2>
              </div>
              <div className="info-block-content">
                <p>
                  {t('aboutGameText')}
                </p>
              </div>
            </div>

            <div className="info-block">
              <div className="info-block-header">
                <h2>{t('howToPlayTitle')}</h2>
              </div>
              <div className="info-block-content">
                <p>
                  {t('howToPlayText')}
                </p>
                <h3 style={{ marginTop: '1.5rem', fontSize: '1.1rem', fontWeight: 'bold' }}>{t('callToActionTitle')}</h3>
                <p style={{ fontStyle: 'italic' }}>
                  {t('callToActionText')}
                </p>
                <div style={{ marginTop: '1.5rem', height: '450px' }}>
                  <iframe 
                    style={{ border: '1px solid rgba(0, 0, 0, 0.1)', width: '100%', height: '100%', borderRadius: '4px' }}
                    src="https://embed.figma.com/proto/sg5frVxZ600k8N5tJxRJVd/Green-Transions---Product?page-id=633%3A482&node-id=633-569&viewport=1990%2C2631%2C0.12&scaling=min-zoom&content-scaling=fixed&starting-point-node-id=633%3A569&embed-host=share" 
                    allowFullScreen={true}
                    title="Green Transitions Game"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
};

export default ProjectResults;
